import {Injectable} from '@angular/core';
import {Layout} from '@app/@core/@models/advertising/layout/layout.model';
import {ProductSelectionData, TargetingFilterGroupForUi, TargetingSelectionData} from '@app/@core/@models/network/network-data.model';
import {forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {ProductResource} from '@app/@core/@rest/design/product-resource.service';
import {ConfigService} from '@app/@core/@config/config.service';
import {NetworkDataResource} from '@app/@core/@rest/admin/network-data-resource.service';
import {LayoutsResource} from '@app/@core/@rest/design/layouts-resource.service';
import {Product} from '@app/@core/@models/design/product.model';
import {ResultList} from '@app/@core/@models/common/result-list';
import {AnonSelfServiceResource} from '@app/@core/@rest/anon/anon-self-service-resource.service';
import {PersistenceQueryOptions} from '@app/@core/@models/persistence/persistence-query-options';
import {CouponResource} from '@app/@core/@rest/design/coupons-resource.service';
import {Coupon} from '@app/@core/@models/design/coupon.model';
import {LineItemTemplate} from "@app/@core/@models/design/line-item-template.model";
import { SiteGroupAndSite, SiteGroupResource } from '@app/@core/@rest/design/site-group-resource.service';

export type ProductData = {
  products: ResultList<Product>,
  layouts: ResultList<Layout>,
  productSelectionData?: ProductSelectionData,
  siteGroupsAndSites?: Array<SiteGroupAndSite>
  targetingFilterGroupForUi: TargetingFilterGroupForUi[],
  signUpCoupons?: ResultList<Coupon>
};

@Injectable({
  providedIn: 'root'
})
export class ProductDataHelper {

  constructor(private productResource: ProductResource,
              private siteGroupResource: SiteGroupResource,
              private configService: ConfigService,
              private networkDataResource: NetworkDataResource,
              private layoutsResource: LayoutsResource,
              private anonSelfServiceResource: AnonSelfServiceResource,
              private couponResource: CouponResource) {
  }

  getProductData$(props: { anon: boolean }): Observable<ProductData> {
    return forkJoin([
      this.getProducts$(props),
      this.getProductFilter$(props),
      this.getTargetingFilter$(props),
      this.getLineItemTemplates$(),
      this.signUpCoupons(props.anon),
      this.getSiteGroupsAndSites$(props)
    ]).pipe(
      switchMap(([products, filterList, targetingFilter, lineItemTemplates, signUpCoupons, siteGroupsAndSites]) => this.getLayouts$(props, products).pipe(
          map((layouts) => {
              const tFilterGroups = targetingFilter.results;
              const tGroupsForUi: TargetingFilterGroupForUi[] = [];
              if (tFilterGroups.length > 0) {
                // should only be one of these
                const tFilters = tFilterGroups[0].data.filter;

                tFilters.forEach(tfe => {
                  const matchingProductIds = products.results.filter(p => {
                    return tfe.products.indexOf(p.id) > -1;
                  });
                  const matchingLineItemTemplates = lineItemTemplates.results.filter(lit => {
                    return tfe.options.find(tfo => tfo.lineItemTemplate === lit.id);
                  });
                  if (matchingProductIds.length > 0 && matchingLineItemTemplates.length > 0) {
                    const hydratedTargetingOptions = tfe.options.map(tfeo => {
                      return {value: tfeo.value, description: tfeo.description, lineItemTemplate: matchingLineItemTemplates.find(mlit => mlit.id === tfeo.lineItemTemplate)};
                    });
                    tGroupsForUi.push({key: tfe.key, productIds: matchingProductIds.map((mp) => {return mp.id}), targetingFilterOptions: hydratedTargetingOptions});
                  }
                });
              }
              return ({products, productSelectionData: this.getFilter(filterList), siteGroupsAndSites: siteGroupsAndSites, targetingFilterGroupForUi: tGroupsForUi, layouts, signUpCoupons})
            }
          )
        )
      )
    );
  }

  private getSiteGroupsAndSites$(props: { anon: boolean }): Observable<Array<SiteGroupAndSite>> {
    const siteParams = {
      context: this.configService.getNetworkData()!.id,
      orderBy: 'name',
      pageSize: 301,
    };
    return props.anon ? of([]) : this.siteGroupResource.getSiteGroupAndSites(siteParams);
  }

  private getProducts$(props: { anon: boolean }): Observable<ResultList<Product>> {
    const productQueryParams = {
      context: this.configService.getNetworkData()!.id,
      excludeInvalid: true,
      includeActive: true,
      includeHidden: false,
      includeInactive: false,
      orderBy: 'name',
      pageSize: 301,
    };
    return props.anon ? this.anonSelfServiceResource.getProducts$(productQueryParams) : this.productResource.get(productQueryParams);
  }

  private getLineItemTemplates$(): Observable<ResultList<LineItemTemplate>> {
    return this.anonSelfServiceResource.getLineItemTemplates$();
  }

  private getTargetingFilter$(props: { anon: boolean }): Observable<ResultList<TargetingSelectionData>> {
    return props.anon ? this.anonSelfServiceResource.getTargetingFilters$() : this.networkDataResource.getTargetingSelectionData();
  }

  private getProductFilter$(props: { anon: boolean }): Observable<ResultList<ProductSelectionData>> {
    return props.anon ? this.anonSelfServiceResource.getProductFilters$() : this.networkDataResource.getProductSelectionData();
  }

  private getLayouts$(props: { anon: boolean }, products: ResultList<Product>): Observable<ResultList<Layout>> {
    const options = {
      includeActive: true,
      includeHidden: false,
      includeInactive: false,
      orderBy: 'name',
    } as PersistenceQueryOptions;
    const layoutIdSet = new Set(products.results.flatMap(product => product.layouts.map(layout => layout.id)));
    options.pageSize = layoutIdSet.size;
    return props.anon ?
      this.anonSelfServiceResource.getLayouts$(options, [...layoutIdSet])
      : this.layoutsResource.getLayouts$(options, [...layoutIdSet]);
  }

  private getTargetingFilter(filters: ResultList<TargetingSelectionData>): TargetingSelectionData | undefined {
    return filters.results.find((filter) => filter.type === 'targetingSelection');
  }

  private getFilter(filters: ResultList<ProductSelectionData>): ProductSelectionData | undefined {
    return filters.results.find((filter) => filter.type === 'productSelection');
  }

  private signUpCoupons(anon: boolean): Observable<ResultList<Coupon> | undefined> {
    if (anon) {
      return of(undefined);
    } else {
      return this.couponResource.signupCoupon();
    }
  }
}
